import { mode, transparentize } from '@chakra-ui/theme-tools';
const sizes = {
    sm: {
        height: '1.875rem',
        minWidth: '1.875rem',
        fontSize: 'sm',
        px: 3,
    },
    md: {
        height: '2.25rem',
        minWidth: '2.25rem',
        fontSize: 'md',
        px: 4,
    },
    lg: {
        height: '2.625rem',
        minWidth: '2.625rem',
        fontSize: 'lg',
        px: 5,
    },
    xl: {
        height: '3rem',
        minWidth: '3rem',
        fontSize: 'xl',
        paddingX: 6,
    },
    '2xl': {
        height: '3.375rem',
        minWidth: '3.375rem',
        fontSize: 'xl',
        paddingX: 6,
    },
};
const baseStyle = {
    lineHeight: '1.2',
    borderRadius: 'base',
    fontWeight: 'semibold',
    transition: 'background 0.3s ease-in-out',
    _focus: {
        boxShadow: 'outline',
    },
    ':focus:not(:focus-visible)': {
        boxShadow: 'none',
    },
    ':focus:not(:-moz-focusring)': {
        boxShadow: 'none',
    },
    _disabled: {
        opacity: 1,
        cursor: 'not-allowed',
        boxShadow: 'none',
    },
};
const defaultProps = {
    size: 'md',
    variant: 'solid',
    colorScheme: 'blue',
};
const graySolidStyle = (props) => {
    const { theme } = props;
    return {
        bg: mode(transparentize('gray.600', 0.28)(theme), transparentize('white', 0.36)(theme))(props),
        _hover: {
            bg: mode(`gray.200`, `whiteAlpha.300`)(props),
        },
        _active: {
            bg: mode(`gray.300`, `whiteAlpha.400`)(props),
        },
    };
};
const getSolidStyle = (props) => {
    const { colorScheme, theme } = props;
    if (colorScheme === 'gray') {
        return graySolidStyle(props);
    }
    const disabledBackground = mode(transparentize('gray.300', 0.16)(theme), transparentize('blue.100', 0.08)(theme))(props);
    return {
        bg: mode(`${colorScheme}.500`, `${colorScheme}.400`)(props),
        color: mode(`white`, `rgba(0, 0, 0, 0.87)`)(props),
        _hover: {
            bg: mode(`${colorScheme}.600`, `${colorScheme}.300`)(props),
            _disabled: {
                bg: disabledBackground,
            },
        },
        _active: { bg: mode(`${colorScheme}.700`, `${colorScheme}.200`)(props) },
        _disabled: {
            color: mode(transparentize('gray.600', 0.36)(theme), transparentize('white', 0.36)(theme))(props),
            bg: disabledBackground,
        },
        _focus: {
            boxShadow: mode(`0 0 0 1px ${transparentize(`${colorScheme}.600`, 1.0)(theme)}, 0 0 0 5px ${transparentize(`${colorScheme}.400`, 0.24)(theme)}`, `0 0 0 1px ${transparentize(`${colorScheme}.300`, 1.0)(theme)}, 0 0 0 5px ${transparentize(`${colorScheme}.400`, 0.24)(theme)}`)(props),
        },
    };
};
const grayGhostStyle = (props) => ({
    color: mode(`inherit`, 'white')(props),
    _hover: {
        bg: transparentize('blue.300', 0.12)(props.theme),
    },
    _active: {
        bg: transparentize('blue.300', 0.24)(props.theme),
    },
    _disabled: {
        color: mode(transparentize('gray.600', 0.36)(props.theme), transparentize('white', 0.36)(props.theme))(props),
        _hover: {
            bg: 'transparent',
        },
    },
    _focus: {
        boxShadow: mode(`0 0 0 1px ${transparentize('blue.600', 1.0)(props.theme)}, 0 0 0 5px ${transparentize('blue.400', 0.24)(props.theme)}`, `0 0 0 1px ${transparentize('blue.300', 1.0)(props.theme)}, 0 0 0 5px ${transparentize('blue.400', 0.24)(props.theme)}`)(props),
    },
});
const getGhostStyle = (props) => {
    const { colorScheme, theme } = props;
    if (colorScheme === 'gray') {
        return grayGhostStyle(props);
    }
    return {
        color: mode(`${colorScheme}.500`, `${colorScheme}.200`)(props),
        bg: 'transparent',
        _hover: {
            bg: transparentize(`${colorScheme}.300`, 0.12)(theme),
        },
        _active: {
            bg: transparentize(`${colorScheme}.300`, 0.24)(theme),
        },
        _focus: {
            boxShadow: `0 0 0 1px ${transparentize(`${colorScheme}.600`, 1.0)(theme)}, 0 0 0 5px ${transparentize(`${colorScheme}.400`, 0.24)(theme)}`,
        },
        _disabled: {
            color: mode(transparentize('gray.600', 0.36)(theme), transparentize('white', 0.36)(theme))(props),
            _hover: {
                bg: 'transparent',
            },
        },
    };
};
const getOutlineStyle = (props) => {
    const { theme } = props;
    return {
        ...getGhostStyle(props),
        border: '1px solid',
        borderColor: mode(transparentize('gray.500', 0.24)(theme), transparentize('white', 0.24)(theme))(props),
        _disabled: {
            ...getGhostStyle(props)._disabled,
            borderColor: mode(transparentize('gray.500', 0.16)(theme), transparentize('blue.100', 0.08)(theme))(props),
        },
    };
};
const getInactiveStyle = (props) => {
    const { theme } = props;
    const outlineStyles = getOutlineStyle(props);
    if (props.colorMode === 'light') {
        return {
            ...outlineStyles,
            color: transparentize('gray.600', 0.67)(theme),
        };
    }
    const disabledBackground = transparentize('blue.100', 0.08)(theme);
    return {
        ...outlineStyles,
        bg: transparentize('gray.450', 0.8)(theme),
        color: 'white',
        borderColor: 'transparent',
        _hover: {
            bg: 'gray.450',
            _disabled: { bg: disabledBackground },
        },
        _active: { bg: 'gray.500' },
        _disabled: {
            color: outlineStyles._disabled.color,
            bg: disabledBackground,
        },
        _focus: {
            boxShadow: `0 0 0 1px ${transparentize('gray.400', 1.0)(props.theme)}, 0 0 0 5px ${transparentize('blue.400', 0.24)(props.theme)}`,
        },
    };
};
const variants = {
    solid: getSolidStyle,
    outline: getOutlineStyle,
    ghost: getGhostStyle,
    inactive: getInactiveStyle,
};
export default {
    baseStyle,
    sizes,
    variants,
    defaultProps,
};
//# sourceMappingURL=button.js.map